import React, { useEffect, useState } from 'react';
import TickerMarquee from './marquee-with-slider';
import { useInView } from 'react-intersection-observer';
import Icon from '@mdi/react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { BQLink } from '../../../atoms/bqlink';
import { useInterval } from '../../../../helpers/custom-hooks';
import { mdiArrowTopRightThick, mdiArrowBottomLeftThick, mdiChevronRight } from '@mdi/js';
import { getUserStatus } from '../../../../helpers/utils';
import { isWithinTradingHours } from '../../../../helpers/utils';

import './market-ticker-nav.m.css';

const MarketTickerItems = ({ pageType, userData }) => {
  const marketTicker = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'market_ticker', 'enabled'], false),
  );
  const api_endpoint = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'ndtv_header', 'api_endpoints'], ''),
  );
  const { ref, inView } = useInView({ threshold: 0, rootMargin: '0px' });
  const [isMobile, setIsMobile] = useState(false);
  const [initialCall, setInitialCall] = useState(true);
  const [marketTickerData, setMarketTickerData] = useState([]);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getTickerData = async () => {
    await fetch(api_endpoint?.marketStrip)
      .then((res) => res.json())
      .then((response) => {
        const data = response?.data || [];
        setMarketTickerData([...data]);
      })
      .catch((err) => {
        console.error('Ticker API Error:', err);
        setMarketTickerData([]); // fallback to prevent crashes
      });
  };

  // Auto refresh every 45 seconds
  useInterval(() => {
    if (marketTicker && inView && isWithinTradingHours(tradingHolidays, specialTradingDays)) {
      getTickerData();
    }
  }, 45000);

  // Initial load
  useEffect(() => {
    if (initialCall && inView) {
      getTickerData();
      setInitialCall(false);
    }
  }, [inView]);

  const handleClick = (tickerName, pageType) => {
    const baseData = {
      event: 'trending_interaction',
      cta_text: tickerName || 'NA',
      section_name: 'market',
      user_id: userData?.userProfile?.userId || 'NA',
      user_status: getUserStatus(),
      login_type: userData?.isLoggedIn ? 'logged-in' : 'logged-out',
      page_template: pageType || 'NA',
      language_hit: 'english',
      Platform: isMobile ? 'mobile' : 'desktop',
      SiteName: 'www.ndtvprofit.com',
    };

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push(baseData);
    } else {
      console.error('Error: window.dataLayer is not available');
    }
  };

  if (!marketTicker) return null;

  const items = marketTickerData?.map((ticker, idx) => {
    const movement = ticker?.priceMovementDto || {};
    const chg = movement.chg ?? 0;
    const chgp = movement.chgp ?? 0;
    const current_price = movement.current_price ?? '--';

    const isUp = chg > 0;
    const formattedValue = `${isUp ? '+' : ''}${chg} (${chgp}%)`;

    return (
      <div
        key={idx}
        styleName="ticker-item"
        className="ticker-container"
        onClick={() => handleClick(get(ticker, ['name'], ''), pageType)}
      >
        <BQLink styleName="ticker-content" href="/markets?src=mar_ticker">
          {ticker?.sname || '--'} <span styleName="price">{current_price}</span>
          {isUp ? (
            <Icon path={mdiArrowTopRightThick} size="14px" color="var(--positive)" />
          ) : (
            <Icon path={mdiArrowBottomLeftThick} size="14px" color="var(--negative)" />
          )}
          <span styleName={isUp ? 'up' : 'down'}>{formattedValue}</span>
        </BQLink>
        {/* <div styleName="pipe">|</div> */}
      </div>
    );
  });

  return (
    <div ref={ref} styleName="ticker-wrapper">
      <TickerMarquee
        items={items}
        heading={
          !isMobile && (
            <div className="desktop-only" styleName="desktop-heading">
              View Markets
              <Icon path={mdiChevronRight} size="16px" color="var(--grey-icon)" />
            </div>
          )
        }
        showToggle={false}
        speed={40}
        isMobile={isMobile}
      />
    </div>
  );
};

export default MarketTickerItems;
